import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9onteiqs38sj6jc7c90';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState<string>('');

  useEffect(() => {
    document.title = '关于我们 - 星星动漫网 | 品牌介绍与联系方式';
  }, []);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('contact_alt') as string;
    if (honeypot && honeypot.trim().length > 0) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('contact_alt');

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError(serverMsg);
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试。');
    }
  };

  const testimonials = [
    {
      name: '小林子',
      avatar: 'https://readdy.ai/api/search-image?query=young%20Asian%20person%20portrait%20simple%20clean%20studio%20lighting%20neutral%20expression%20minimalist%20white%20background&width=100&height=100&seq=avatar-01&orientation=squarish',
      text: '星星动漫网真是太好了！韩漫资源超全，画质也很清晰，下拉式阅读体验比翻页舒服多了。每天必刷的网站！',
      rating: 5,
    },
    {
      name: '漫画爱好者小王',
      avatar: 'https://readdy.ai/api/search-image?query=Asian%20teen%20portrait%20casual%20style%20clean%20background%20simple%20lighting%20minimalist%20photography&width=100&height=100&seq=avatar-02&orientation=squarish',
      text: '从日漫入坑到现在已经很多年了，这里的日漫分类做得特别好，找作品很方便。推荐给了我身边所有的漫友！',
      rating: 5,
    },
    {
      name: '追更达人',
      avatar: 'https://readdy.ai/api/search-image?query=young%20woman%20portrait%20simple%20clean%20background%20soft%20lighting%20neutral%20expression%20minimalist&width=100&height=100&seq=avatar-03&orientation=squarish',
      text: '更新速度很快，国漫资源也越来越丰富了。界面简洁好看，没有乱七八糟的广告，阅读体验非常棒。',
      rating: 5,
    },
  ];

  const faqs = [
    {
      q: '星星动漫网的所有漫画都是免费的吗？',
      a: '是的！星星动漫网上的所有漫画作品均为免费在线阅读，无需注册或付费即可畅享全部内容。',
    },
    {
      q: '支持哪些设备阅读？',
      a: '星星动漫网全面适配桌面端、平板和手机浏览器，无论您使用什么设备都能获得流畅的阅读体验。推荐使用Chrome或Safari浏览器。',
    },
    {
      q: '漫画更新频率如何？',
      a: '我们会在漫画官方更新后第一时间进行同步，通常连载中的热门作品每周都会有更新，确保您不错过任何精彩内容。',
    },
    {
      q: '如何搜索我想看的漫画？',
      a: '您可以通过顶部的搜索框输入作品名称或作者名进行搜索，也可以通过首页的分类入口按韩漫、日漫、国漫进行浏览。',
    },
    {
      q: '阅读时遇到问题怎么办？',
      a: '如果遇到页面加载缓慢或图片无法显示等问题，请尝试刷新页面或清除浏览器缓存。如问题持续存在，欢迎通过下方的联系表单反馈给我们。',
    },
  ];

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '星星动漫网',
    url: 'https://www.xingxingdongman.com/',
    description: '星星动漫网漫画大全，汇集韩漫、日漫、国漫等各类好看的漫画，免费全集在线观看。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@xingxingdongman.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Navbar />

      <main className="min-h-screen bg-background-50">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <section className="py-10" aria-labelledby="about-heading">
            <h1 id="about-heading" className="font-heading text-3xl font-bold text-foreground-900 mb-4">
              关于星星动漫网
            </h1>
            <p className="text-foreground-600 text-base max-w-3xl leading-relaxed">
              星星动漫网是一个专注于漫画在线阅读的平台，致力于为中文漫画爱好者提供最优质的阅读体验。
              我们汇集了韩漫、日漫、国漫等各类漫画作品，涵盖热血、恋爱、奇幻、搞笑等多种类型，
              让每一位读者都能在这里找到自己喜欢的漫画。
            </p>
          </section>

          <section className="py-10 border-t border-background-200/70" aria-labelledby="story-heading">
            <h2 id="story-heading" className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              品牌故事
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  星星动漫网创立于2020年，由一群热爱漫画的年轻人共同创办。我们深知漫画爱好者对于高质量阅读体验的渴望，
                  因此从一开始就坚持以用户为中心，精心筛选每一部作品，确保内容的丰富性和画面的高清品质。
                </p>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  经过多年的发展，星星动漫网已经从一个小众漫画站成长为拥有数百万活跃用户的漫画阅读平台。
                  我们的漫画大全涵盖了来自韩国、日本和中国的主流漫画作品，无论是热门连载还是经典完结作品，都能在这里找到。
                </p>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  未来，我们将继续扩展漫画资源库，优化阅读体验，让好看的漫画触手可及。
                  星星动漫网，让每一个漫画爱好者都能享受阅读的乐趣。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=cozy%20modern%20reading%20nook%20with%20bookshelves%20warm%20lighting%20comfortable%20chair%20manga%20comics%20displayed%20on%20shelves%20artistic%20lifestyle%20photography%20soft%20natural%20light%20harmonious%20composition&width=800&height=500&seq=about-story-2026&orientation=landscape"
                  alt="星星动漫网品牌故事"
                  title="星星动漫网 - 品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section id="reputation" className="py-10 border-t border-background-200/70" aria-labelledby="reputation-heading">
            <h2 id="reputation-heading" className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-100/60 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.text}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5 text-center">
              {[
                { num: '10万+', label: '漫画资源' },
                { num: '500万+', label: '活跃用户' },
                { num: '99.9%', label: '好评率' },
                { num: '3万+', label: '日均新增' },
              ].map((s) => (
                <div key={s.label} className="bg-background-100/60 rounded-lg p-6">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{s.num}</div>
                  <div className="text-sm text-foreground-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section id="faq" className="py-10 border-t border-background-200/70" aria-labelledby="faq-heading">
            <h2 id="faq-heading" className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              常见问题
            </h2>
            <div className="space-y-3 max-w-3xl">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-100/60 rounded-lg overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between px-5 py-4 list-none">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section id="contact" className="py-10 border-t border-background-200/70" aria-labelledby="contact-heading">
            <h2 id="contact-heading" className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，
                  或填写右侧的联系表单，我们会在24小时内回复您。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">contact@xingxingdongman.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100">
                      <i className="ri-wechat-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信公众号</p>
                      <p className="text-sm font-medium text-foreground-900">星星动漫网</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100">
                      <i className="ri-weibo-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">官方微博</p>
                      <p className="text-sm font-medium text-foreground-900">@星星动漫网</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {formStatus === 'success' ? (
                  <div className="bg-background-100/60 rounded-lg p-8 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                      <i className="ri-check-line text-2xl text-accent-600"></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">提交成功！</h3>
                    <p className="text-sm text-foreground-600">感谢您的留言，我们会在24小时内回复您。</p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="whitespace-nowrap mt-4 px-6 py-2 rounded-full bg-background-200 text-foreground-700 text-sm hover:bg-background-300 transition-colors cursor-pointer"
                    >
                      发送新消息
                    </button>
                  </div>
                ) : (
                  <form
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleContactSubmit}
                    className="bg-background-100/60 rounded-lg p-6 space-y-4"
                  >
                    {formStatus === 'error' && formError && (
                      <div className="bg-accent-100/50 border border-accent-200/50 rounded-md px-4 py-3 text-sm text-accent-700">
                        {formError}
                      </div>
                    )}
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言（不超过500字）"
                      ></textarea>
                    </div>
                    <div className="honeypot-form-field" aria-hidden="true">
                      <input
                        type="text"
                        name="contact_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="opacity-0 absolute top-[-9999px] left-[-9999px]"
                      />
                    </div>
                    <button
                      type="submit"
                      className="whitespace-nowrap w-full py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      发送消息
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}